import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, Check, X } from 'lucide-react';

const RoleSelectorModal = ({ isOpen, onClose }) => {
  const { user, selectRole } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Get current role info
  const currentRole = user?.selectedRole;
  const allRoles = user?.roles || [];
  
  // Filter out duplicate roles and normalize role names
  const availableRoles = allRoles
    .map(role => {
      const roleName = role.RoleName || role;
      return typeof roleName === 'string' ? roleName : String(roleName);
    })
    .filter((roleName, index, self) => self.indexOf(roleName) === index) // Remove duplicates
    .sort(); // Sort alphabetically
  
  const hasMultipleRoles = availableRoles.length > 1;
  
  // Helper function to get role display name
  const getRoleDisplayName = (roleName) => {
    // Capitalize first letter and add spaces before capital letters
    return roleName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle role selection
  const handleRoleSelect = async (roleName) => {
    const currentRoleName = currentRole?.RoleName || currentRole;
    
    console.log('🔍 ROLE_SELECTOR_MODAL: Starting role selection', {
      selectedRole: roleName,
      currentRole: currentRoleName,
      isLoading
    });
    
    if (roleName === currentRoleName || isLoading) {
      console.log('🔍 ROLE_SELECTOR_MODAL: Skipping selection - same role or loading');
      return;
    }

    let safetyTimer;
    try {
      setIsLoading(true);
      console.log('🔍 ROLE_SELECTOR_MODAL: Calling selectRole with:', roleName);

      // Safety: reset loading if backend hangs
      safetyTimer = setTimeout(() => {
        console.warn('⏱️ ROLE_SELECTOR_MODAL: Role switch timeout - resetting loading state');
        setIsLoading(false);
      }, 10000);
      
      // Switch role
      const result = await selectRole(roleName);
      console.log('🔍 ROLE_SELECTOR_MODAL: selectRole result:', result);
      
      if (!result?.success) {
        setIsLoading(false);
        return;
      }
      
      // Close modal
      onClose();
      
      // Refresh profile to update user context with new role
      // This will trigger re-renders in all components that use useAuth()
      console.log(`🔍 ROLE_SELECTOR_MODAL: Role switched to: ${roleName}. Refreshing profile...`);
      
      // Use a small delay to ensure the role is saved before refreshing
      setTimeout(async () => {
        try {
          // Import refreshProfile dynamically to avoid circular dependency
          const { useAuth } = await import('../../contexts/AuthContext');
          // We can't use hooks here, so we'll reload the page
          // But first, let's trigger a custom event that components can listen to
          window.dispatchEvent(new CustomEvent('roleChanged', { detail: { roleName } }));
          
          // Reload the page to ensure all components get the updated role
          window.location.reload();
        } catch (error) {
          console.error('Error refreshing after role change:', error);
          window.location.reload();
        }
      }, 100);
      
    } catch (error) {
      console.error('🔍 ROLE_SELECTOR_MODAL: Error switching role:', error);
      setIsLoading(false);
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
    }
  };

  // Don't render if no roles available or only one role
  if (!isOpen || availableRoles.length === 0 || !hasMultipleRoles) {
    return null;
  }

  // Get display role (current selected or first available)
  const displayRole = currentRole?.RoleName || currentRole || availableRoles[0];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Switch Role
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={isLoading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select a role to switch your current permissions and access level.
            </p>
            
            <div className="space-y-2">
              {availableRoles.map((roleName) => {
                const isSelected = roleName === displayRole;
                return (
                  <button
                    key={roleName}
                    onClick={() => handleRoleSelect(roleName)}
                    disabled={isLoading}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm rounded-lg transition-colors ${
                      isSelected 
                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-2 border-purple-200 dark:border-purple-800' 
                        : 'text-gray-700 dark:text-gray-300 border-2 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <Shield className={`h-5 w-5 ${
                        isSelected 
                          ? 'text-purple-600 dark:text-purple-400' 
                          : 'text-gray-400 dark:text-gray-500'
                      }`} />
                      <div className="text-left">
                        <div className="font-medium">{getRoleDisplayName(roleName)}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {roleName}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    )}
                  </button>
                );
              })}
            </div>

            {isLoading && (
              <div className="mt-4 flex items-center justify-center space-x-2 text-purple-600 dark:text-purple-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 dark:border-purple-400"></div>
                <span className="text-sm">Switching role...</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleSelectorModal;

